/**
 * Turn a caught API failure into user-facing copy.
 *
 * The server-api sends STABLE MACHINE CODES as the `{ error }` body — `internal` (any unhandled
 * 500), `needs-human`, `guardrail`, `lock`, `drift`, `clavero-disabled`, `payload too large`, … —
 * and the API client surfaces that raw code as `Error.message`. Rendering it verbatim leaks
 * developer shorthand to the user (a live incident: the Shopping view showed the literal word
 * "internal"). `internal` is deliberately opaque server-side so it never exposes a stack trace or a
 * Firestore index URL, so the humanizing has to happen here on the client.
 *
 * Control flow keys off `err.status` (never the message text), so rewriting the message for display
 * is safe. Known codes map to friendly sentences; genuine server sentences (ValidationError,
 * Unauthorized/Forbidden messages, …) fall through unchanged.
 */

// Named copy constants so the status-based fallbacks can reference the exact same strings as the
// code map without a `string | undefined` index lookup (the repo runs noUncheckedIndexedAccess).
const INTERNAL = 'Something went wrong on our end. Please try again in a moment — if it keeps happening, it’s on us.';
const SIGNED_OUT = 'You’re signed out. Sign in and try again.';
const TOO_LARGE = 'That file or request is too large.';
// The API client rejects with status 0 when the device is known-offline (airplane mode, dead Wi-Fi).
// Reads still render from the local cache, so this is advisory copy for a user-triggered action.
const OFFLINE = 'You’re offline. Showing what’s saved on this device — it’ll sync when you reconnect.';

/** Known opaque codes → the sentence a user should actually read. */
const FRIENDLY_ERROR_COPY: Record<string, string> = {
  internal: INTERNAL,
  'needs-human': 'This needs a quick human check (a sign-in or CAPTCHA) before it can continue. Complete it, then try again.',
  guardrail: 'A safety guardrail blocked this request.',
  drift: 'Something changed unexpectedly, so we paused to stay safe. Refresh and try again.',
  lock: 'Another action is already in progress. Give it a moment, then try again.',
  'clavero-disabled': 'Amazon shopping is turned off. Enable it in Settings → Integrations → Amazon.',
  'no-stash': 'There’s nothing stashed to restore.',
  'intent-not-found': 'That order draft couldn’t be found — it may have expired. Try again.',
  'no-confirm-token': 'Add your Amazon confirm token in Settings → Integrations → Amazon to place orders.',
  'clavero-health-check-failed': 'Couldn’t reach Amazon shopping. Check the connection in Settings → Integrations → Amazon, then try again.',
  'payload too large': TOO_LARGE,
  'not signed in': SIGNED_OUT,
  offline: OFFLINE,
};

/** Extract an HTTP status if the error carries one (e.g. the client's `ApiError`), without importing it. */
function statusOf(err: unknown): number | undefined {
  const s = (err as { status?: unknown } | null)?.status;
  return typeof s === 'number' ? s : undefined;
}

/**
 * The friendly, user-facing string for a caught error. Use this wherever a caught API error is put
 * into visible UI (banners, toasts, inline notes) instead of `err.message` / `String(err)`.
 */
export function humanizeApiError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  const mapped = FRIENDLY_ERROR_COPY[raw];
  if (mapped) return mapped;

  // Status-based fallbacks for codes we didn't name explicitly.
  const status = statusOf(err);
  if (status === 0) return OFFLINE;
  if (status === 401) return SIGNED_OUT;
  if (status === 413) return TOO_LARGE;
  if (status !== undefined && status >= 500) return INTERNAL;

  // Genuine server sentences (validation messages, etc.) read fine as-is.
  return raw || 'Something went wrong.';
}
